import { format, type Locale, type Day } from 'date-fns';
import { sv, enUS, nb } from 'date-fns/locale';
import { useMemo } from 'react';

import { isDateDefined } from '../utils/trips';

export type DateFormats = typeof dateFormats;

export const dateFormats = {
  TIME: 'HH:mm',
  DATE_SE: 'yyyy-MM-dd',
  DATE_NO: 'dd.MM.yyyy',
  DATE_US: 'd MMM yyyy',
  DAY_MONTH_SHORT: 'd MMM',
};

export interface WeekDays {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
  7: string;
}

export const useDateTimeFormat = (currentLanguage: string) => {
  const timeFormat = dateFormats.TIME;

  const dateTimeFormat = useMemo(() => {
    if (currentLanguage === 'sv') return 'yyyy-MM-dd HH:mm';
    else if (currentLanguage === 'no') return 'dd.MM.yyyy HH:mm';
    return 'd MMM yyyy HH:mm';
  }, [currentLanguage]);

  const dateFormat = useMemo(() => {
    if (currentLanguage === 'sv') return dateFormats.DATE_SE;
    else if (currentLanguage === 'no') return dateFormats.DATE_NO;
    return dateFormats.DATE_US;
  }, [currentLanguage]);

  const locale = useMemo((): Locale => {
    if (currentLanguage === 'sv') return sv;
    else if (currentLanguage === 'no') return nb;
    return enUS;
  }, [currentLanguage]);

  const formatDate = (date: number | Date, customFormat?: string): string => {
    return format(date, customFormat ?? dateFormat, {
      locale,
    });
  };

  const formatTime = (
    dateTime: Date | number,
    customFormat?: string
  ): string => {
    return format(dateTime, customFormat ?? timeFormat);
  };

  const formatDateTime = (dateTime: Date | number, customFormat?: string) => {
    return format(dateTime, customFormat ?? dateTimeFormat);
  };

  /** Same as `formatDateTime`, but returns `fallback` for missing or invalid values. */
  const formatDateTimeOrFallback = (
    value: string | null | undefined,
    fallback = '-',
    customFormat?: string
  ): string => {
    if (!isDateDefined(value)) return fallback;
    const dateTime = new Date(value);
    if (Number.isNaN(dateTime.getTime())) return fallback;
    return formatDateTime(dateTime, customFormat);
  };

  /** Same as `formatDate`, but returns `fallback` instead of the Unix epoch when `value` is missing. */
  const formatDateOrFallback = (
    value: string | null | undefined,
    fallback = '-',
    customFormat?: string
  ): string => {
    if (!isDateDefined(value)) return fallback;
    return formatDate(new Date(value), customFormat);
  };

  /** Same as `formatTime`, but returns `fallback` instead of the Unix epoch when `value` is missing. */
  const formatTimeOrFallback = (
    value: string | null | undefined,
    fallback = '-',
    customFormat?: string
  ): string => {
    if (!isDateDefined(value)) return fallback;
    return formatTime(new Date(value), customFormat);
  };

  const weekDays = useMemo(() => {
    // date-fns always return days starting with sunday, regardless of locale
    const days: string[] = [...Array(7).keys()].map(
      (i) => locale?.localize?.day(i as Day) as string
    );
    // return days with week starting on monday
    return {
      1: days[1],
      2: days[2],
      3: days[3],
      4: days[4],
      5: days[5],
      6: days[6],
      7: days[0],
    } as WeekDays;
  }, [locale?.localize]);

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatDateTimeOrFallback,
    formatDateOrFallback,
    formatTimeOrFallback,
    dateFormat,
    timeFormat,
    dateTimeFormat,
    weekDays,
    dateFormats,
  };
};
